import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { PublicQuote } from '@wrapforce/shared';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { publicApi } from '@/lib/publicApi';
import { useLocaleFormatters } from '@/lib/format';
import { timeOptionsForDate } from '@/lib/booking';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Dialog } from '@/components/ui/dialog';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';
import { BeforeAfterSlider } from '@/components/BeforeAfterSlider';
import { DepositCard } from './DepositCard';

// §34.4 - a ceramic coating line shows the before/after slider. Engine-
// priced lines carry serviceKind directly; legacy manual lines (pre-§29,
// no serviceKind) are matched by serviceName as a fallback since that's all
// they have.
function hasCeramicLine(lineItems: PublicQuote['lineItems']): boolean {
  return lineItems.some((li) => li.serviceKind === 'ceramic' || (!li.serviceKind && /ceramic/i.test(li.serviceName)));
}

function CeramicBeforeAfterSection() {
  const { t } = useTranslation('publicQuote');
  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-surface p-5">
      <div className="text-sm font-bold text-ink">{t('ceramicBeforeAfter.title')}</div>
      <p className="mt-1 text-xs text-muted">{t('ceramicBeforeAfter.caption')}</p>
      <div className="mt-3 overflow-hidden rounded-[var(--radius-input)]">
        <BeforeAfterSlider
          beforeSrc="/before-after/ceramic-before.jpg"
          afterSrc="/before-after/ceramic-after.jpg"
          beforeLabel={t('ceramicBeforeAfter.beforeLabel')}
          afterLabel={t('ceramicBeforeAfter.afterLabel')}
          beforeAlt={t('ceramicBeforeAfter.beforeAlt')}
          afterAlt={t('ceramicBeforeAfter.afterAlt')}
          ariaLabel={t('ceramicBeforeAfter.sliderAriaLabel')}
          width={1600}
          height={900}
        />
      </div>
    </div>
  );
}

function MediaCarousel({ media }: { media: PublicQuote['media'] }) {
  const { t } = useTranslation('publicQuote');
  const [index, setIndex] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const touchStartX = useRef<number | null>(null);

  if (media.length === 0) return null;

  function go(delta: number) {
    setIndex((i) => (i + delta + media.length) % media.length);
  }

  function handleTouchStart(e: React.TouchEvent) {
    touchStartX.current = e.touches[0]!.clientX;
  }

  function handleTouchEnd(e: React.TouchEvent) {
    if (touchStartX.current === null) return;
    const deltaX = e.changedTouches[0]!.clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(deltaX) < 40) return;
    go(deltaX < 0 ? 1 : -1);
  }

  return (
    <>
      <div className="relative h-56 bg-ink" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        <img
          src={media[index]!.url}
          alt=""
          onClick={() => setFullscreen(true)}
          className="h-full w-full cursor-zoom-in object-cover"
        />
        {media.length > 1 ? (
          <>
            <button
              onClick={() => go(-1)}
              aria-label={t('media.previousImage')}
              className="absolute left-2 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-full bg-black/40 text-white"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => go(1)}
              aria-label={t('media.nextImage')}
              className="absolute right-2 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-full bg-black/40 text-white"
            >
              <ChevronRight size={18} />
            </button>
            <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1.5">
              {media.map((m, i) => (
                <button
                  key={m.id}
                  onClick={() => setIndex(i)}
                  aria-label={t('media.showImage', { index: i + 1 })}
                  className={`h-1.5 w-1.5 rounded-full ${i === index ? 'bg-white' : 'bg-white/40'}`}
                />
              ))}
            </div>
          </>
        ) : null}
      </div>

      {fullscreen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95"
          onClick={() => setFullscreen(false)}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <button
            onClick={() => setFullscreen(false)}
            aria-label={t('media.close')}
            className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full bg-black/40 text-white"
          >
            <X size={20} />
          </button>
          <img src={media[index]!.url} alt="" onClick={(e) => e.stopPropagation()} className="max-h-full max-w-full object-contain" />
          {media.length > 1 ? (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  go(-1);
                }}
                aria-label={t('media.previousImage')}
                className="absolute left-4 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-black/40 text-white"
              >
                <ChevronLeft size={22} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  go(1);
                }}
                aria-label={t('media.nextImage')}
                className="absolute right-4 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-black/40 text-white"
              >
                <ChevronRight size={22} />
              </button>
            </>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

// §34.2 - customer-facing preferred booking date/time picker plus a free-form
// details field (brand preference, questions), shown while the quote is still
// sent/viewed. There is no save button: values are reported up via onChange
// and submitted when the customer accepts (or pays the deposit).
export interface BookingPreference {
  preferredDate: string | null;
  preferredTime: number | null;
  customerNote: string;
}

function PreferredBookingSection({ quote, onChange }: { quote: PublicQuote; onChange: (value: BookingPreference) => void }) {
  const { t } = useTranslation('publicQuote');
  const { formatTimeOfDay } = useLocaleFormatters();
  const [preferredDate, setPreferredDate] = useState<string | null>(quote.preferredDate);
  const [preferredTime, setPreferredTime] = useState<number | null>(quote.preferredTime);
  const [customerNote, setCustomerNote] = useState(quote.customerRequestNote);

  const timeOptions = preferredDate ? timeOptionsForDate(quote.operatingHours, preferredDate) : [];

  // Runs on mount too, so the parent's ref is seeded with the saved values
  // even if the customer never touches the fields before accepting.
  useEffect(() => {
    onChange({ preferredDate, preferredTime, customerNote });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferredDate, preferredTime, customerNote]);

  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-surface p-5">
      <div className="mb-3 text-sm font-bold text-ink">{t('preferredBooking.title')}</div>
      <div className="flex gap-2.5">
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="publicPreferredDate">{t('preferredBooking.dateLabel')}</Label>
          <Input
            id="publicPreferredDate"
            type="date"
            min={quote.windowStart}
            // Native date inputs only open the picker from the tiny calendar
            // icon; customers expect a click anywhere in the field to open it.
            onClick={(e) => e.currentTarget.showPicker?.()}
            className="cursor-pointer"
            value={preferredDate ?? ''}
            onChange={(e) => {
              setPreferredDate(e.target.value || null);
              setPreferredTime(null);
            }}
          />
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="publicPreferredTime">{t('preferredBooking.timeLabel')}</Label>
          <select
            id="publicPreferredTime"
            value={preferredTime ?? ''}
            disabled={!preferredDate}
            onChange={(e) => setPreferredTime(e.target.value ? Number(e.target.value) : null)}
            className="h-10 border border-border bg-surface px-2 text-sm disabled:opacity-50"
          >
            <option value="">{t('preferredBooking.noTimeSelected')}</option>
            {timeOptions.map((m) => (
              <option key={m} value={m}>{formatTimeOfDay(m)}</option>
            ))}
          </select>
        </div>
      </div>
      {preferredDate && timeOptions.length === 0 ? (
        <p className="mt-2 text-xs text-muted">{t('preferredBooking.closedDay')}</p>
      ) : null}
      <p className="mt-2 text-xs text-muted">{t('preferredBooking.disclaimer')}</p>
      <div className="mt-3 flex flex-col gap-1.5">
        <Label htmlFor="publicCustomerNote">{t('preferredBooking.detailsLabel')}</Label>
        <textarea
          id="publicCustomerNote"
          value={customerNote}
          onChange={(e) => setCustomerNote(e.target.value)}
          placeholder={t('preferredBooking.detailsPlaceholder')}
          rows={3}
          maxLength={1000}
          className="w-full rounded-[var(--radius-input)] border border-border bg-surface px-3 py-2 text-sm outline-none focus-visible:border-accent"
        />
      </div>
      <p className="mt-2 text-xs text-muted">{t('preferredBooking.submittedOnAccept')}</p>
    </div>
  );
}

// Replaces a native prompt() for the decline reason - the app never uses
// alert/confirm/prompt, only its own Dialog + toast pattern.
function DeclineDialog({
  open,
  submitting,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  submitting: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => void;
}) {
  const { t } = useTranslation('publicQuote');
  const [reason, setReason] = useState('');

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setReason('');
        onOpenChange(next);
      }}
      title={t('declineDialog.title')}
      description={t('declineDialog.description')}
    >
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder={t('declineDialog.placeholder')}
        rows={3}
        maxLength={300}
        className="w-full rounded-[var(--radius-input)] border border-border bg-surface px-3 py-2 text-sm outline-none focus-visible:border-accent"
      />
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={() => onOpenChange(false)}>
          {t('declineDialog.cancel')}
        </Button>
        <Button variant="danger" loading={submitting} onClick={() => onConfirm(reason)}>
          {t('declineDialog.confirm')}
        </Button>
      </div>
    </Dialog>
  );
}

function usePaymentReturnPoll(token: string | undefined, isPaidReturn: boolean, onSettled: () => void) {
  useEffect(() => {
    if (!token || !isPaidReturn) return;
    let cancelled = false;
    let elapsed = 0;
    const interval = setInterval(async () => {
      elapsed += 2000;
      const { status } = await publicApi.get<{ status: string }>(`/api/v1/public/quotes/${token}/status`);
      if (cancelled) return;
      if (status === 'deposit_paid' || status === 'converted' || elapsed >= 60_000) {
        clearInterval(interval);
        onSettled();
      }
    }, 2000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isPaidReturn]);
}

export function PublicQuotePage() {
  const { t } = useTranslation('publicQuote');
  const { formatMoney, formatDate } = useLocaleFormatters();
  const { token } = useParams<{ token: string }>();
  const [params] = useSearchParams();
  const [declined, setDeclined] = useState(false);
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [declining, setDeclining] = useState(false);
  // Latest booking preference/details from PreferredBookingSection - held in
  // a ref (not state) since nothing re-renders on change; it's read once at
  // accept/pay time.
  const bookingRef = useRef<BookingPreference | null>(null);
  const queryClient = useQueryClient();
  const { data: quote, isLoading, isError } = useQuery({
    queryKey: ['public-quote', token],
    queryFn: () => publicApi.get<PublicQuote>(`/api/v1/public/quotes/${token}`),
    enabled: !!token,
    retry: false,
  });

  usePaymentReturnPoll(token, params.get('paid') === '1', () => {
    void queryClient.invalidateQueries({ queryKey: ['public-quote', token] });
  });

  if (isLoading) return <div className="grid min-h-screen place-items-center text-muted">{t('loading')}</div>;
  if (isError || !quote) {
    return (
      <div className="relative grid min-h-screen place-items-center px-6 text-center">
        <div className="absolute right-4 top-4">
          <LanguageSwitcher syncToProfile={false} />
        </div>
        <div>
          <h1 className="font-display text-xl font-bold">{t('invalidLink.title')}</h1>
          <p className="mt-2 text-sm text-muted">{t('invalidLink.body')}</p>
        </div>
      </div>
    );
  }

  async function handleDeclineConfirm(reason: string) {
    setDeclining(true);
    try {
      await publicApi.post(`/api/v1/public/quotes/${token}/decline`, { reason: reason.trim() || undefined });
      setDeclineDialogOpen(false);
      setDeclined(true);
      toast.success(t('toast.declined'));
    } finally {
      setDeclining(false);
    }
  }

  function bookingPayload() {
    const b = bookingRef.current;
    if (!b) return {};
    return { preferredDate: b.preferredDate, preferredTime: b.preferredTime, customerNote: b.customerNote.trim() };
  }

  // Accepting IS the save: the preferred date/time and details go up with
  // the accept call, there is no separate "save preference" step.
  async function handleAccept() {
    await publicApi.post(`/api/v1/public/quotes/${token}/accept`, bookingPayload());
    await queryClient.invalidateQueries({ queryKey: ['public-quote', token] });
    toast.success(t('toast.accepted'));
  }

  // Deposit flow equivalent: persist the preference/details right before the
  // Stripe payment confirms, since paying is how those quotes get accepted.
  async function saveBookingBeforePay() {
    await publicApi.post(`/api/v1/public/quotes/${token}/preference`, bookingPayload());
  }

  return (
    <div className="min-h-screen bg-bg px-4 py-8">
      <div className="mx-auto flex max-w-lg flex-col gap-3.5">
        <div className="flex items-center gap-2.5 px-1">
          {quote.shop.logoUrl ? (
            <img src={quote.shop.logoUrl} alt="" className="h-9 w-9 object-cover" />
          ) : (
            <div className="grid h-9 w-9 place-items-center bg-accent text-sm font-bold text-on-accent">{quote.shop.name.slice(0, 2).toUpperCase()}</div>
          )}
          <div>
            <div className="text-sm font-bold text-ink">{quote.shop.name}</div>
            {quote.location ? (
              <div className="text-xs text-muted">
                {quote.location.name}
                {quote.location.address ? ` · ${quote.location.address}` : ''}
              </div>
            ) : null}
          </div>
          <div className="ml-auto flex items-center gap-2.5">
            <span className="font-mono text-xs text-muted">Q-{quote.number}</span>
            <LanguageSwitcher syncToProfile={false} />
          </div>
        </div>

        <div className="rounded-[var(--radius-card)] border border-border bg-surface">
          <div className="border-b-2 border-ink p-5">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted">{t('quoteFor')}</div>
            <div className="mt-1 text-lg font-bold text-ink">{quote.customerFirstName}</div>
            <div className="text-sm text-muted">{[quote.vehicle.year, quote.vehicle.make, quote.vehicle.model, quote.vehicle.trim].filter(Boolean).join(' ')}</div>
          </div>

          <MediaCarousel media={
            quote.media.filter((m) => m.kind === 'print_render').length > 0
              ? quote.media.filter((m) => m.kind === 'print_render')
              : quote.media.filter((m) => m.kind === 'mockup').length > 0
                ? quote.media.filter((m) => m.kind === 'mockup')
                : quote.media
          } />

          <div className="flex flex-col gap-2.5 p-5">
            {quote.lineItems.map((li, i) => (
              <div key={i} className="flex justify-between text-sm">
                <div>
                  <div className="font-semibold text-ink">{li.serviceName} - {li.coverageName}</div>
                  {li.inventoryName ? <div className="text-xs text-muted">{li.inventoryName}</div> : null}
                  <div className="text-xs text-muted">{li.materialBrand} {li.materialSeries}{li.color ? ` · ${li.color}` : ''}</div>
                  {li.estimatedMinutes != null ? (
                    <div className="text-xs text-muted">{t('estimatedTime', { hours: (li.estimatedMinutes / 60).toFixed(1) })}</div>
                  ) : null}
                </div>
                <div className="font-mono font-semibold">{formatMoney(li.lineTotalCents)}</div>
              </div>
            ))}
            <div className="flex justify-between border-t border-border pt-3 text-sm text-muted">
              <span>{t('subtotal')}</span>
              <span className="font-mono font-semibold text-ink">{formatMoney(quote.subtotalCents)}</span>
            </div>
            {quote.discountCents > 0 ? (
              <div className="flex justify-between text-sm text-muted">
                <span>{quote.discountName || t('discount')} · −{quote.discountPct}%</span>
                <span className="font-mono font-semibold text-ink">−{formatMoney(quote.discountCents)}</span>
              </div>
            ) : null}
            {quote.taxCents > 0 ? (
              <div className="flex justify-between text-sm text-muted">
                <span>{t('tax')}</span>
                <span className="font-mono font-semibold text-ink">{formatMoney(quote.taxCents)}</span>
              </div>
            ) : null}
            <div className="flex justify-between border-t border-border pt-3">
              <span className="font-bold">{t('total')}</span>
              <span className="font-mono text-xl font-bold">{formatMoney(quote.totalCents)}</span>
            </div>
          </div>
        </div>

        {hasCeramicLine(quote.lineItems) ? <CeramicBeforeAfterSection /> : null}

        {!declined && (quote.status === 'sent' || quote.status === 'viewed') ? (
          <PreferredBookingSection quote={quote} onChange={(value) => { bookingRef.current = value; }} />
        ) : null}

        <div className="rounded-[var(--radius-card)] border border-border bg-surface p-5">
          {declined ? (
            <div className="text-sm text-muted">{t('declined')}</div>
          ) : quote.status === 'deposit_paid' || quote.status === 'converted' ? (
            <div className="flex flex-col gap-1.5">
              <div className="text-base font-bold text-success">
                {quote.depositCents > 0 ? t('depositPaidBooked') : t('quoteAcceptedBooked')}
              </div>
              <p className="text-sm text-muted">{t('shopWillBeInTouch')}</p>
            </div>
          ) : quote.depositCents <= 0 ? (
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-bold text-ink">{t('acceptThisQuote')}</div>
                <div className="text-xs text-muted">{t('noDepositRequired')}</div>
              </div>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => setDeclineDialogOpen(true)}>{t('rejectQuote')}</Button>
                <Button onClick={() => void handleAccept()}>{t('acceptQuote')}</Button>
              </div>
            </div>
          ) : params.get('paid') === '1' ? (
            <div className="flex items-center justify-center gap-2 py-2 text-sm text-muted">
              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-accent border-t-transparent" />
              {t('processingPayment')}
            </div>
          ) : !quote.paymentsReady ? (
            <div className="rounded-[var(--radius-input)] border border-warning/30 bg-warning/10 px-3.5 py-2.5 text-sm text-warning">
              {t('onlineDepositUnavailable', { shopName: quote.shop.name })}
            </div>
          ) : token ? (
            <DepositCard token={token} depositCents={quote.depositCents} shopName={quote.shop.name} onBeforePay={saveBookingBeforePay} />
          ) : null}
        </div>

        {!declined && quote.depositCents > 0 && quote.status !== 'deposit_paid' && quote.status !== 'converted' ? (
          <Button variant="secondary" onClick={() => setDeclineDialogOpen(true)}>
            {t('rejectQuote')}
          </Button>
        ) : null}

        <div className="flex items-center justify-center gap-1.5 text-center text-[11px] text-muted">
          {quote.validUntil ? <span>{t('validUntil', { date: formatDate(new Date(quote.validUntil), 'MMM d, yyyy') })} · </span> : null}
          {t('poweredBy')} <span className="font-bold italic">WrapForce</span>
        </div>
      </div>

      <DeclineDialog
        open={declineDialogOpen}
        submitting={declining}
        onOpenChange={setDeclineDialogOpen}
        onConfirm={(reason) => void handleDeclineConfirm(reason)}
      />
    </div>
  );
}
